// src/components/Printables.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  doc,
  writeBatch,
  serverTimestamp,
  deleteField,
  setDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';
import { firestore, storage } from '../firebase';
import { buildRaffleTicketPrintBarcodeValue } from '../utils/raffleTicketBarcode';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import { getDisplayName, getFirstName } from '../utils/flyerDisplay';
import { SKYQUEST_FORMATIONS, SKYQUEST_LOGOS } from '../constants/skyQuestConfig';
import {
  openPrintWindow,
  buildSkyQuestPdf,
  getAvery5163BadgeCss,
  getAvery5163BagLabelCss,
  getAvery5874BusinessCardCss,
  getSkyQuestCardCss,
  getLogoUrl,
} from '../utils/printUtils';
import './Printables.css';
import { fetchSponsorsMergedForEvent } from '../utils/sponsorQueries';
import {
  ensureMainClueEventConfigMigrated,
  subscribeMainClueEventConfig,
} from '../utils/mainClueEventConfig';

const PRINT_TYPES = {
  BADGES: 'badges',
  BAG_LABELS: 'bagLabels',
  RAFFLE: 'raffle',
  GENERAL_RAFFLE: 'generalRaffle',
  PLACARDS: 'placards',
  SKYQUEST_FRESHLING_CARDS: 'skyQuestFreshlingCards',
  PASSCODE_CARDS: 'passcodeCards',
  BUCKET_FLYERS: 'bucketFlyers',
  EVENT_SIGN: 'eventSign',
  ENVELOPE_LABELS: 'envelopeLabels',
};

const PASSCODE_CARDS_PER_SHEET = 10;
const DEFAULT_MAX_TEAM_NUMBER = 62;

const LABELS_PER_SHEET = 10;

/** CODE128 barcodes as PNG data URLs for print HTML (one per flyer id). */
async function buildRaffleBarcodeDataUrlByFlyerId(flyerIds) {
  const unique = [...new Set((flyerIds || []).filter(Boolean))];
  const out = {};
  for (const flyerId of unique) {
    const payload = buildRaffleTicketPrintBarcodeValue(flyerId);
    if (!payload) continue;
    const canvas = document.createElement('canvas');
    try {
      JsBarcode(canvas, payload, {
        format: 'CODE128',
        displayValue: true,
        fontSize: 10,
        height: 56,
        width: 2.2,
        margin: 2,
      });
      out[flyerId] = canvas.toDataURL('image/png');
    } catch {
      out[flyerId] = '';
    }
  }
  return out;
}

function Printables() {
  const [searchParams] = useSearchParams();
  const eventIdFromUrl = searchParams.get('eventId') || '';

  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [eventsLoading, setEventsLoading] = useState(true);
  const [flyers, setFlyers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [mainClueSetAssignments, setMainClueSetAssignments] = useState({});
  const [teamPasscodes, setTeamPasscodes] = useState({}); // { teamNum: passcode }
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState('');
  const [printType, setPrintType] = useState(PRINT_TYPES.BADGES);
  const [filterUnprinted, setFilterUnprinted] = useState(false);
  const [filterType, setFilterType] = useState('all'); // all | freshling | mentor | volunteer
  const [filterShirtMismatch, setFilterShirtMismatch] = useState(false);
  const [maxTeamNumber, setMaxTeamNumber] = useState(DEFAULT_MAX_TEAM_NUMBER);
  const [skyQuestPointValue, setSkyQuestPointValue] = useState(0.5);
  const [skyQuestBlankWinners, setSkyQuestBlankWinners] = useState(5);
  const [skyQuestFormationCounts, setSkyQuestFormationCounts] = useState({});
  const [skyQuestPreviewCard, setSkyQuestPreviewCard] = useState(null);
  const [skyQuestFormations, setSkyQuestFormations] = useState([]);
  const [skyQuestLogoUrl, setSkyQuestLogoUrl] = useState((SKYQUEST_LOGOS.skyQuestLogoUrl || '').trim());
  const [freshMeetSkyQuestLogoUrl, setFreshMeetSkyQuestLogoUrl] = useState(
    (SKYQUEST_LOGOS.freshMeetLogoUrl || SKYQUEST_LOGOS.freshMeatLogoUrl || '').trim() || getLogoUrl()
  );
  const [newSkyQuestFormationName, setNewSkyQuestFormationName] = useState('');
  const [creatingSkyQuestFormation, setCreatingSkyQuestFormation] = useState(false);
  const [skyQuestFormationMessage, setSkyQuestFormationMessage] = useState('');
  const [skyQuestPdfLoading, setSkyQuestPdfLoading] = useState(false);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [printType, filterUnprinted, filterType, filterShirtMismatch, selectedEventId]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [blankCount, setBlankCount] = useState(5);
  const [updating, setUpdating] = useState(false);

  const currentEvent = useMemo(
    () => events.find((ev) => ev.id === selectedEventId) || null,
    [events, selectedEventId]
  );

  useEffect(() => {
    let cancelled = false;
    async function loadEvents() {
      setEventsLoading(true);
      try {
        const [snap, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEvents(list);
        if (list.length) {
          setSelectedEventId(
            resolveAdminDefaultEventId({
              eventIdParam: eventIdFromUrl,
              activeEventId: activeId,
              events: list,
            })
          );
        }
      } catch (e) {
        if (!cancelled) setDataError(e.message || 'Failed to load events.');
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    }
    loadEvents();
    return () => {
      cancelled = true;
    };
  }, [eventIdFromUrl]);

  useEffect(() => {
    if (!selectedEventId) return;
    setDataLoading(true);
    setDataError('');
    (async () => {
      try {
        const [
          flyersSnap,
          teamsSnap,
          passcodesSnap,
          prizesSnap,
          sponsorsSnap,
          skyQuestConfigSnap,
          skyQuestFormationsSnap,
        ] = await Promise.all([
          getDocs(
            query(
              collection(firestore, 'flyers'),
              where('eventId', '==', selectedEventId)
            )
          ),
          getDocs(
            query(
              collection(firestore, 'teams'),
              where('eventId', '==', selectedEventId)
            )
          ),
          getDoc(doc(firestore, 'teamPasscodes', selectedEventId)),
          getDocs(
            query(
              collection(firestore, 'prizes'),
              where('eventId', '==', selectedEventId)
            )
          ),
          fetchSponsorsMergedForEvent(selectedEventId),
          getDoc(doc(firestore, 'skyQuestConfig', 'global')),
          getDocs(collection(firestore, 'skyQuestFormations')),
        ]);
        setFlyers(flyersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setTeams(teamsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setPrizes(prizesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setSponsors(sponsorsSnap);
        const passcodesData = passcodesSnap.exists() ? passcodesSnap.data() : {};
        const passcodes = passcodesData.passcodes && typeof passcodesData.passcodes === 'object'
          ? passcodesData.passcodes
          : {};
        setTeamPasscodes(passcodes);
        if (skyQuestConfigSnap.exists()) {
          const cfg = skyQuestConfigSnap.data() || {};
          const sq = (cfg.skyQuestLogoUrl || '').trim();
          const fm = (cfg.freshMeetLogoUrl || cfg.freshMeatLogoUrl || '').trim();
          setSkyQuestLogoUrl(sq);
          setFreshMeetSkyQuestLogoUrl(fm || (SKYQUEST_LOGOS.freshMeetLogoUrl || SKYQUEST_LOGOS.freshMeatLogoUrl || '').trim() || getLogoUrl());
        }
        const formations = skyQuestFormationsSnap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            formationName: data.formationName || data.name || '',
            formationImageUrl: data.formationImageUrl || data.imageUrl || '',
          };
        });
        setSkyQuestFormations(formations);
      } catch (e) {
        setDataError(e.message || 'Failed to load data.');
      } finally {
        setDataLoading(false);
      }
    })();
  }, [selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) {
      setMainClueSetAssignments({});
      return;
    }
    let cancelled = false;
    let unsub = () => {};
    (async () => {
      try {
        await ensureMainClueEventConfigMigrated(selectedEventId);
        if (cancelled) return;
        unsub = subscribeMainClueEventConfig(selectedEventId, (data) => {
          setMainClueSetAssignments(data.setAssignments || {});
        });
      } catch {
        if (!cancelled) setMainClueSetAssignments({});
      }
    })();
    return () => {
      cancelled = true;
      unsub();
    };
  }, [selectedEventId]);

  const flyersByEmail = useMemo(() => {
    const map = new Map();
    flyers.forEach((f) => {
      const e = (f.email || '').toString().trim().toLowerCase();
      if (e) map.set(e, f);
    });
    return map;
  }, [flyers]);

  const teamPairingByEmail = useMemo(() => {
    const map = new Map();
    teams.forEach((t) => {
      const num = (t.teamNumber || '').toString().trim();
      const name = (t.teamName || '').toString().trim();
      const label = name ? `Team ${num} – ${name}` : (num ? `Team ${num}` : '');
      if (!label) return;
      const mentorEmail = (t.mentor || '').toString().trim().toLowerCase();
      if (mentorEmail) map.set(mentorEmail, label);
      (t.freshlings || []).forEach((e) => {
        const key = (e || '').toString().trim().toLowerCase();
        if (key) map.set(key, label);
      });
    });
    return map;
  }, [teams]);

  const isAcceptedMentor = useCallback((f) => {
    if (f.type !== 'mentor') return true;
    if (f.cancelled) return false;
    const st = (f.status || '').toString().trim().toLowerCase();
    return st.includes('accept');
  }, []);

  const isPrinted = (item, type) => {
    if (type === PRINT_TYPES.BUCKET_FLYERS) return false;
    if (type === PRINT_TYPES.PLACARDS) {
      return !!item.teamPlacardPrintedAt;
    }
    const ps = item.printStatus || {};
    if (type === PRINT_TYPES.BADGES) return !!ps.badgesPrintedAt;
    if (type === PRINT_TYPES.BAG_LABELS) return !!ps.bagLabelsPrintedAt;
    if (type === PRINT_TYPES.RAFFLE) return !!ps.raffleTicketsPrintedAt;
    if (type === PRINT_TYPES.GENERAL_RAFFLE) return !!ps.generalRaffleTicketsPrintedAt;
    if (type === PRINT_TYPES.SKYQUEST_FRESHLING_CARDS) return !!ps.freshlingCardsPrintedAt;
    return false;
  };

  const bucketFlyerRows = useMemo(() => {
    return prizes
      .filter((p) => (p.giveawayType || '') === 'bucket_raffle')
      .map((p) => ({
        ...p,
        sponsor: sponsors.find((s) => s.id === p.sponsorId) || null,
      }));
  }, [prizes, sponsors]);

  const rows = useMemo(() => {
    if (printType === PRINT_TYPES.EVENT_SIGN || printType === PRINT_TYPES.ENVELOPE_LABELS) {
      return [];
    }
    if (printType === PRINT_TYPES.BUCKET_FLYERS) {
      return bucketFlyerRows;
    }
    if (printType === PRINT_TYPES.SKYQUEST_FRESHLING_CARDS) {
      const filtered = flyers
        .filter((f) => f && f.type === 'freshling' && !f.cancelled)
        .filter(isAcceptedMentor)
        .sort((a, b) => {
          const fa = (getFirstName(a) || '').toLowerCase();
          const fb = (getFirstName(b) || '').toLowerCase();
          return fa.localeCompare(fb, undefined, { sensitivity: 'base' });
        });
      if (filterUnprinted) return filtered.filter((r) => !isPrinted(r, PRINT_TYPES.SKYQUEST_FRESHLING_CARDS));
      return filtered;
    }
    if (printType === PRINT_TYPES.PLACARDS) {
      const list = teams
        .filter((t) => t.teamNumber && (t.mentor || (t.freshlings && t.freshlings.length)))
        .map((t) => {
          const mentorEmail = (t.mentor || '').toString().trim().toLowerCase();
          const mentorFlyer = mentorEmail ? flyersByEmail.get(mentorEmail) : null;
          const mentorName = mentorFlyer
            ? getDisplayName(mentorFlyer) || mentorFlyer.name || mentorFlyer.email || t.mentor
            : t.mentor || '—';
          const freshlingNames = (t.freshlings || [])
            .map((e) => {
              const f = flyersByEmail.get(e.toString().trim().toLowerCase());
              return f ? getDisplayName(f) || f.name || f.email : e;
            })
            .filter(Boolean);
          return {
            id: t.id,
            teamNumber: t.teamNumber,
            teamName: (t.teamName || '').trim(),
            division: (t.division || '').trim(),
            mentorName,
            freshlingNames,
            teamPlacardPrintedAt: t.teamPlacardPrintedAt,
          };
        })
        .sort((a, b) => (Number(a.teamNumber) || 0) - (Number(b.teamNumber) || 0));
      if (filterUnprinted) return list.filter((r) => !isPrinted(r, PRINT_TYPES.PLACARDS));
      return list;
    }

    const includeVolunteers = printType === PRINT_TYPES.BAG_LABELS;
    let list = flyers
      .filter((f) => {
        if (!f || f.cancelled) return false;
        if (f.type === 'freshling' || f.type === 'mentor') return true;
        return includeVolunteers && f.type === 'volunteer';
      })
      .filter((f) => (f.type === 'volunteer' ? true : isAcceptedMentor(f)))
      .map((f) => ({ ...f }));
    if (filterType === 'freshling') list = list.filter((f) => f.type === 'freshling');
    else if (filterType === 'mentor') list = list.filter((f) => f.type === 'mentor');
    else if (filterType === 'volunteer') list = list.filter((f) => f.type === 'volunteer');

    if (printType === PRINT_TYPES.BADGES) {
      list = [...list].sort((a, b) => {
        const typeOrder = (t) => (t === 'freshling' ? 0 : t === 'mentor' ? 1 : 2);
        const ta = typeOrder(a.type);
        const tb = typeOrder(b.type);
        if (ta !== tb) return ta - tb;
        const firstA = getFirstName(a) || '';
        const firstB = getFirstName(b) || '';
        return firstA.toLowerCase().localeCompare(firstB.toLowerCase(), undefined, { sensitivity: 'base' });
      });
    }

    if (printType === PRINT_TYPES.BAG_LABELS) {
      list = [...list].sort((a, b) => {
        const typeOrder = (t) =>
          t === 'freshling' ? 0 : t === 'mentor' ? 1 : t === 'volunteer' ? 2 : 3;
        const ta = typeOrder(a.type);
        const tb = typeOrder(b.type);
        if (ta !== tb) return ta - tb;
        const firstA = (a.type === 'volunteer' ? (a.name || '').trim().split(/\s+/)[0] : getFirstName(a)) || '';
        const firstB = (b.type === 'volunteer' ? (b.name || '').trim().split(/\s+/)[0] : getFirstName(b)) || '';
        return firstA.toLowerCase().localeCompare(firstB.toLowerCase(), undefined, { sensitivity: 'base' });
      });
      if (filterShirtMismatch) {
        const norm = (v) => (v == null ? '' : String(v).trim().toUpperCase());
        list = list.filter((f) => {
          const requested = norm(f.tShirtRequested) || norm(f.tShirtSize);
          const allocated = norm(f.tShirtAllocated);
          return allocated && requested && allocated !== requested;
        });
      }
    }
    if (printType === PRINT_TYPES.RAFFLE) {
      const filtered = list.filter((f) => f.type === 'freshling');
      if (filterUnprinted) return filtered.filter((r) => !isPrinted(r, PRINT_TYPES.RAFFLE));
      return filtered;
    }
    if (printType === PRINT_TYPES.GENERAL_RAFFLE) {
      const filtered = list.filter((f) => f.type === 'freshling');
      if (filterUnprinted) return filtered.filter((r) => !isPrinted(r, PRINT_TYPES.GENERAL_RAFFLE));
      return filtered;
    }
    if (filterUnprinted) return list.filter((r) => !isPrinted(r, printType));
    return list;
  }, [
    flyers,
    teams,
    printType,
    filterUnprinted,
    filterType,
    filterShirtMismatch,
    flyersByEmail,
    bucketFlyerRows,
    isAcceptedMentor,
  ]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === rows.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map((r) => r.id)));
    }
  };

  const getStatusField = useCallback(() => {
    if (printType === PRINT_TYPES.BADGES) return 'badgesPrintedAt';
    if (printType === PRINT_TYPES.BAG_LABELS) return 'bagLabelsPrintedAt';
    if (printType === PRINT_TYPES.RAFFLE) return 'raffleTicketsPrintedAt';
    if (printType === PRINT_TYPES.GENERAL_RAFFLE) return 'generalRaffleTicketsPrintedAt';
    if (printType === PRINT_TYPES.SKYQUEST_FRESHLING_CARDS) return 'freshlingCardsPrintedAt';
    return null;
  }, [printType]);

  const markAsPrinted = async (idsToMark) => {
    if (!idsToMark.length) return;
    setUpdating(true);
    try {
      const batch = writeBatch(firestore);
      if (printType === PRINT_TYPES.PLACARDS) {
        idsToMark.forEach((id) => {
          batch.update(doc(firestore, 'teams', id), {
            teamPlacardPrintedAt: serverTimestamp(),
          });
        });
      } else {
        const field = getStatusField();
        if (field) {
          idsToMark.forEach((id) => {
            batch.update(doc(firestore, 'flyers', id), {
              [`printStatus.${field}`]: serverTimestamp(),
            });
          });
        }
      }
      await batch.commit();
      const now = new Date();
      if (printType === PRINT_TYPES.PLACARDS) {
        setTeams((prev) =>
          prev.map((t) =>
            idsToMark.includes(t.id)
              ? { ...t, teamPlacardPrintedAt: now }
              : t
          )
        );
      } else {
        const field = getStatusField();
        if (field) {
          setFlyers((prev) =>
            prev.map((f) =>
              idsToMark.includes(f.id)
                ? {
                    ...f,
                    printStatus: {
                      ...(f.printStatus || {}),
                      [field]: now,
                    },
                  }
                : f
            )
          );
        }
      }
      setSelectedIds((prev) => {
        const next = new Set(prev);
        idsToMark.forEach((id) => next.delete(id));
        return next;
      });
    } catch (err) {
      console.error('Failed to mark as printed:', err);
    } finally {
      setUpdating(false);
    }
  };

  const resetPrinted = async (idsToReset) => {
    if (!idsToReset.length) return;
    setUpdating(true);
    try {
      const batch = writeBatch(firestore);
      if (printType === PRINT_TYPES.PLACARDS) {
        idsToReset.forEach((id) => {
          batch.update(doc(firestore, 'teams', id), {
            teamPlacardPrintedAt: deleteField(),
          });
        });
      } else {
        const field = getStatusField();
        if (field) {
          idsToReset.forEach((id) => {
            batch.update(doc(firestore, 'flyers', id), {
              [`printStatus.${field}`]: deleteField(),
            });
          });
        }
      }
      await batch.commit();
      if (printType === PRINT_TYPES.PLACARDS) {
        setTeams((prev) =>
          prev.map((t) =>
            idsToReset.includes(t.id)
              ? { ...t, teamPlacardPrintedAt: null }
              : t
          )
        );
      } else {
        const field = getStatusField();
        if (field) {
          setFlyers((prev) =>
            prev.map((f) =>
              idsToReset.includes(f.id)
                ? {
                    ...f,
                    printStatus: {
                      ...(f.printStatus || {}),
                      [field]: undefined,
                    },
                  }
                : f
            )
          );
        }
      }
    } catch (err) {
      console.error('Failed to reset printed:', err);
    } finally {
      setUpdating(false);
    }
  };

  const handleResetSelected = () => {
    const toReset = rows.filter((r) => selectedIds.has(r.id));
    if (toReset.length) resetPrinted(toReset.map((r) => r.id));
  };

  const printedIds = useMemo(() => {
    if (printType === PRINT_TYPES.PLACARDS) {
      return teams.filter((t) => t.teamPlacardPrintedAt).map((t) => t.id);
    }
    const field = getStatusField();
    if (!field) return [];
    if (printType === PRINT_TYPES.BAG_LABELS) {
      let printed = flyers
        .filter(
          (f) =>
            f &&
            !f.cancelled &&
            (f.type === 'freshling' || f.type === 'mentor' || f.type === 'volunteer')
        )
        .filter((f) => (f.type === 'volunteer' ? true : isAcceptedMentor(f)))
        .filter((f) => (f.printStatus || {})[field]);
      if (filterType === 'freshling') printed = printed.filter((f) => f.type === 'freshling');
      else if (filterType === 'mentor') printed = printed.filter((f) => f.type === 'mentor');
      else if (filterType === 'volunteer') printed = printed.filter((f) => f.type === 'volunteer');
      return printed.map((f) => f.id);
    }
    let list = flyers
      .filter((f) => f && (f.type === 'freshling' || f.type === 'mentor') && !f.cancelled)
      .filter(isAcceptedMentor)
      .filter((f) => (f.printStatus || {})[field]);
    if (printType === PRINT_TYPES.SKYQUEST_FRESHLING_CARDS || printType === PRINT_TYPES.GENERAL_RAFFLE) {
      list = list.filter((f) => f.type === 'freshling');
    } else {
      if (filterType === 'freshling') list = list.filter((f) => f.type === 'freshling');
      else if (filterType === 'mentor') list = list.filter((f) => f.type === 'mentor');
    }
    return list.map((f) => f.id);
  }, [printType, flyers, teams, filterType, getStatusField, isAcceptedMentor]);

  const handleResetAll = () => {
    if (!printedIds.length) return;
    if (!window.confirm(`Reset printed status for all ${printedIds.length} printed items?`)) return;
    resetPrinted(printedIds);
    setSelectedIds(new Set());
  };

  const buildBadgesHtml = (items, isBlank = false) => {
    const labelClass = (f) => {
      if (isBlank) return 'avery-5163-label avery-5163-label--blank';
      return f.type === 'mentor'
        ? 'avery-5163-label avery-5163-label--mentor'
        : 'avery-5163-label avery-5163-label--freshling';
    };
    const labelHtml = (f) => {
      const name = isBlank ? '' : getDisplayName(f) || f.name || f.email || '';
      const dz = isBlank ? '' : (f.homeDz || '').trim();
      return `<div class="${labelClass(f)}">
        <div class="label-name">${name || '&nbsp;'}</div>
        ${dz ? `<div class="label-dz">${dz}</div>` : ''}
      </div>`;
    };

    const chunks = [];
    for (let i = 0; i < items.length; i += LABELS_PER_SHEET) {
      const chunk = items.slice(i, i + LABELS_PER_SHEET);
      const labelsHtml = chunk.map((f) => labelHtml(f)).join('');
      chunks.push(`<div class="avery-5163-sheet">${labelsHtml}</div>`);
    }
    return chunks.join('');
  };

  const buildBagLabelsHtml = (items, isBlank = false) => {
    const normalizeSize = (raw) => {
      if (raw == null) return '';
      const t = String(raw).trim().toUpperCase();
      return t || '';
    };
    const getRequestedSize = (f) => {
      if (!f) return '';
      const r = normalizeSize(f.tShirtRequested);
      if (r) return r;
      return normalizeSize(f.tShirtSize);
    };
    /** Label shows only assigned/allocated size from T-Shirt tab (tShirtAllocated). Never use requested. */
    const getDisplaySizeForLabel = (f) => {
      if (!f) return '';
      const raw = f.tShirtAllocated;
      return normalizeSize(raw);
    };
    const escapeForHtml = (s) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const labelClass = (f) => {
      if (isBlank) return 'avery-5163-label avery-5163-label--blank';
      if (f.type === 'volunteer') return 'avery-5163-label avery-5163-label--volunteer';
      return f.type === 'mentor'
        ? 'avery-5163-label avery-5163-label--mentor'
        : 'avery-5163-label avery-5163-label--freshling';
    };
    const labelHtml = (f) => {
      const isVolunteer = f.type === 'volunteer';
      const name = isBlank ? '' : (isVolunteer ? (f.name || f.email || '') : (getDisplayName(f) || f.name || f.email || ''));
      const requested = getRequestedSize(f);
      const allocated = getDisplaySizeForLabel(f);
      const sizeDisplay = isBlank ? '' : allocated;
      const differs = !isVolunteer && sizeDisplay && requested && allocated !== requested;
      const footnote =
        'You registered after the T-shirt guarantee deadline. We did our best to get your size, but it may not be exactly what you requested.';
      const exchangeMsg =
        "If your T-shirt isn't the right size, come to HQ at 3:30. We will allow exchanges for remaining sizes at that time.";
      let sizeBlock = '';
      if (sizeDisplay) {
        if (differs) {
          sizeBlock =
            '<div class="label-size label-size--with-note">' +
            '<span class="label-size-with-star"><span class="label-size-star">*</span> ' +
            escapeForHtml(sizeDisplay) +
            '</span>' +
            '<span class="bag-label-footnote">' +
            escapeForHtml(footnote) +
            '</span></div>';
        } else {
          sizeBlock = '<div class="label-size">' + escapeForHtml(sizeDisplay) + '</div>';
        }
      }
      const goodyByDefaultFlyer = !isVolunteer && (f.type === 'freshling' || f.type === 'mentor');
      const goodyX =
        !isBlank && ((isVolunteer && f.goodyBag) || goodyByDefaultFlyer)
          ? '<span class="bag-label-goody-x" aria-hidden="true">x</span>'
          : '';
      const exchangeBlock =
        isBlank ? '' : '<div class="bag-label-exchange">' + escapeForHtml(exchangeMsg) + '</div>';
      return (
        '<div class="' +
        labelClass(f) +
        '">' +
        goodyX +
        '<div class="label-name">' +
        (escapeForHtml(name || '') || '&nbsp;') +
        '</div>' +
        sizeBlock +
        exchangeBlock +
        '</div>'
      );
    };

    const chunks = [];
    for (let i = 0; i < items.length; i += LABELS_PER_SHEET) {
      const chunk = items.slice(i, i + LABELS_PER_SHEET);
      const labelsHtml = chunk.map((f) => labelHtml(f)).join('');
      chunks.push('<div class="avery-5163-sheet">' + labelsHtml + '</div>');
    }
    return chunks.join('');
  };

  const buildRaffleHtml = (items, isBlank = false, barcodeByFlyerId = null) => {
    const logoUrl = getLogoUrl();
    const footerShort = 'Add tickets to prize buckets for more chances.';

    const escapeForHtml = (s) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const ticketHtml = (name, phone, flyerId) => {
      const nm = name ? escapeForHtml(name) : '&nbsp;';
      const ph = phone ? escapeForHtml(phone) : '&nbsp;';
      const barcodeUrl =
        !isBlank && flyerId && barcodeByFlyerId && barcodeByFlyerId[flyerId]
          ? barcodeByFlyerId[flyerId]
          : '';
      const backInner = barcodeUrl
        ? `<img class="raffle-barcode-img" src="${barcodeUrl}" alt="" />`
        : `<div class="raffle-back-placeholder">No barcode — write name on front</div>`;
      return (
        `<div class="raffle-ticket">` +
        `<div class="raffle-ticket-front">` +
        `<img src="${logoUrl}" alt="" class="raffle-logo" />` +
        `<div class="raffle-title">Fresh Meet Raffle Ticket</div>` +
        `<div class="raffle-name">${nm}</div>` +
        `<div class="raffle-phone">${ph}</div>` +
        `<div class="raffle-footer">${escapeForHtml(footerShort)}</div>` +
        `</div>` +
        `<div class="raffle-ticket-back">${backInner}</div>` +
        `</div>`
      );
    };

    const tickets = [];
    if (isBlank) {
      const count = typeof items === 'number' ? items : items.length;
      for (let i = 0; i < count; i++) {
        tickets.push(ticketHtml('', '', null));
      }
    } else {
      items.forEach((f) => {
        const name = getDisplayName(f) || f.name || f.email || '—';
        const phone = f.phone || '—';
        const fid = f.id || null;
        for (let j = 0; j < 8; j++) {
          tickets.push(ticketHtml(name, phone, fid));
        }
      });
    }

    const ticketsPerPage = 16;
    const pages = [];
    for (let i = 0; i < tickets.length; i += ticketsPerPage) {
      const pageTickets = tickets.slice(i, i + ticketsPerPage);
      pages.push(
        `<div class="raffle-page">
          <div class="raffle-grid">${pageTickets.join('')}</div>
        </div>`
      );
    }

    const raffleCss = `
      .raffle-page { page-break-after: always; padding: 0.2in; box-sizing: border-box; }
      .raffle-page:last-child { page-break-after: auto; }
      .raffle-grid {
        display: grid; grid-template-columns: repeat(4, 1fr); grid-template-rows: repeat(4, 1fr);
        gap: 0.06in; width: 8in; height: 10.5in; box-sizing: border-box;
      }
      .raffle-ticket {
        border: 1px dashed #9ca3af; padding: 0.05in 0.06in; font-size: 7pt; text-align: center;
        page-break-inside: avoid; box-sizing: border-box; overflow: hidden;
        display: flex; flex-direction: column; align-items: stretch; justify-content: space-between;
        min-height: 0;
      }
      .raffle-ticket-front {
        flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; align-items: center;
        justify-content: center;
      }
      .raffle-logo { max-width: 0.45in; height: auto; margin-bottom: 0.03in; flex-shrink: 0; }
      .raffle-title { font-weight: 700; font-size: 7pt; margin-bottom: 0.03in; line-height: 1.1; }
      .raffle-name { font-weight: 600; font-size: 9pt; margin: 0.015in 0; line-height: 1.1; }
      .raffle-phone { font-size: 7pt; margin: 0.015in 0; }
      .raffle-footer { font-size: 5.5pt; color: #6b7280; margin-top: 0.03in; line-height: 1.15; max-width: 100%; }
      .raffle-ticket-back {
        flex-shrink: 0; display: flex; flex-direction: column; align-items: center; justify-content: center;
        padding: 0.04in 0 0.02in; min-height: 0.55in;
      }
      .raffle-back-placeholder { font-size: 5.5pt; color: #9ca3af; line-height: 1.2; padding: 0.04in 0; }
      .raffle-barcode-img { max-width: 100%; width: 100%; height: auto; max-height: 0.72in; object-fit: contain; }
    `;

    return { html: pages.join(''), extraCss: raffleCss };
  };

  const buildGeneralRaffleHtml = (items, isBlank = false) => {
    const logoUrl = getLogoUrl();
    const TICKETS_PER_PAGE = 10;

    const ticketHtml = (name) => `
      <div class="gen-raffle-ticket">
        <img src="${logoUrl}" alt="Fresh Meet" class="gen-raffle-logo" />
        <div class="gen-raffle-title">RAFFLE TICKET</div>
        <div class="gen-raffle-name">${name || '&nbsp;'}</div>
      </div>`;

    const tickets = [];
    if (isBlank) {
      const count = typeof items === 'number' ? items : items.length;
      for (let i = 0; i < count; i++) {
        tickets.push(ticketHtml(''));
      }
    } else {
      items.forEach((f) => {
        const name = getDisplayName(f) || f.name || f.email || '—';
        tickets.push(ticketHtml(name));
      });
    }

    const pages = [];
    for (let i = 0; i < tickets.length; i += TICKETS_PER_PAGE) {
      const pageTickets = tickets.slice(i, i + TICKETS_PER_PAGE);
      pages.push(
        `<div class="gen-raffle-page">
          <div class="gen-raffle-grid">${pageTickets.join('')}</div>
        </div>`
      );
    }

    const css = `
      @page { size: 8.5in 11in; margin: 0; }
      body { margin: 0; padding: 0; }
      .gen-raffle-page { page-break-after: always; width: 8.5in; height: 11in; box-sizing: border-box; padding: 0.5in 0.15in; }
      .gen-raffle-page:last-child { page-break-after: auto; }
      .gen-raffle-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: repeat(5, 1fr);
        gap: 0;
        width: 100%;
        height: 100%;
      }
      .gen-raffle-ticket {
        border: 1px dashed #9ca3af;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 0.12in 0.2in;
        box-sizing: border-box;
        page-break-inside: avoid;
        overflow: hidden;
      }
      .gen-raffle-logo {
        max-width: 0.9in;
        height: auto;
        margin-bottom: 0.06in;
        flex-shrink: 0;
      }
      .gen-raffle-title {
        font-size: 8pt;
        font-weight: 600;
        letter-spacing: 0.12em;
        color: #6b7280;
        margin-bottom: 0.06in;
        text-transform: uppercase;
      }
      .gen-raffle-name {
        font-size: 20pt;
        font-weight: 800;
        line-height: 1.15;
        color: #111827;
        word-break: break-word;
        max-width: 100%;
      }
    `;

    return { html: pages.join(''), extraCss: css };
  };

  const buildPlacardsHtml = (items, isBlank = false) => {
    const cardHtml = (t) => {
      const teamNum = isBlank ? '' : (t.teamNumber || '').toString().trim();
      const mentor = isBlank ? '' : t.mentorName || '';
      const freshlingsList = isBlank ? [] : (t.freshlingNames || []);
      const people = isBlank ? [] : [mentor, ...freshlingsList].filter(Boolean).slice(0, 3);
      const teamLine = teamNum ? `Team ${teamNum}` : '';
      return `<div class="placard-card">
        ${teamLine ? `<div class="placard-team">${teamLine}</div>` : ''}
        ${people.length ? `<div class="placard-people">${people.join(', ')}</div>` : ''}
      </div>`;
    };

    const cardsPerPage = 50;
    const sheets = [];
    for (let i = 0; i < items.length; i += cardsPerPage) {
      const chunk = items.slice(i, i + cardsPerPage);
      sheets.push(`<div class="placard-sheet">${chunk.map((t) => cardHtml(t)).join('')}</div>`);
    }

    const placardCss = `
      .placard-sheet { padding: 0.15in; display: grid; grid-template-columns: repeat(5, 1fr); grid-template-rows: repeat(10, 1fr); gap: 0.08in; page-break-after: always; min-height: 10in; box-sizing: border-box; }
      .placard-sheet:last-child { page-break-after: auto; }
      .placard-card { font-size: 11pt; border: 1px solid #e5e7eb; padding: 0.08in; page-break-inside: avoid; box-sizing: border-box; display: flex; flex-direction: column; justify-content: center; max-height: 5.8em; overflow: hidden; }
      .placard-team { font-weight: 700; font-size: 12pt; line-height: 1.15; margin-bottom: 0.03em; flex-shrink: 0; }
      .placard-people { font-size: 10pt; line-height: 1.15; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }
    `;

    return {
      html: sheets.join(''),
      extraCss: placardCss,
    };
  };

  const formatSkyQuestPointValue = (value) => {
    const v = Number(value);
    if (!Number.isFinite(v)) return '';
    if (v === 0.25) return '¼';
    if (v === 0.5) return '½';
    if (v === 0.75) return '¾';
    return String(v);
  };

  const buildSkyQuestCardHtml = (card, logos) => {
    const skyQuestLogoUrl = (logos.skyQuestLogoUrl || '').trim();
    const freshMeetLogoUrl = (logos.freshMeetLogoUrl || logos.freshMeatLogoUrl || '').trim() || getLogoUrl();
    const formationImageUrl = (card.formationImageUrl || '').trim();
    const name = escapeHtml(card.participantName || '');
    const formationName = escapeHtml(card.formationName || '');
    const pointDisplay = formatSkyQuestPointValue(card.pointValue);
    const leftLogoHtml = skyQuestLogoUrl
      ? `<img src="${escapeHtml(skyQuestLogoUrl)}" alt="SkyQuest" />`
      : '';
    const rightLogoHtml = freshMeetLogoUrl
      ? `<img src="${escapeHtml(freshMeetLogoUrl)}" alt="Fresh Meet" />`
      : '';
    const formationImgHtml = formationImageUrl
      ? `<img src="${escapeHtml(formationImageUrl)}" alt="${formationName || 'Formation'}" />`
      : '';

    let pointHtml = '';
    if (pointDisplay === '¼' || pointDisplay === '½' || pointDisplay === '¾') {
      const map = { '¼': { n: '1', d: '4' }, '½': { n: '1', d: '2' }, '¾': { n: '3', d: '4' } };
      const frac = map[pointDisplay];
      pointHtml = `<span class="skyquest-frac"><span class="skyquest-frac-num">${frac.n}</span><span class="skyquest-frac-den">${frac.d}</span></span>`;
    } else if (pointDisplay) {
      pointHtml = escapeHtml(pointDisplay);
    }

    return `
      <div class="skyquest-card-inner">
        <div class="skyquest-card-border"></div>
        <div class="skyquest-card-corner tl"></div>
        <div class="skyquest-card-corner tr"></div>
        <div class="skyquest-card-corner bl"></div>
        <div class="skyquest-card-corner br"></div>
        <div class="skyquest-card-content">
          <div class="skyquest-logo-row">
            ${leftLogoHtml}
            ${rightLogoHtml}
          </div>
          <div class="skyquest-formation-panel">
            ${formationImgHtml}
          </div>
          <div class="skyquest-bottom-bar">
            <div class="skyquest-participant-name">${name || '&nbsp;'}</div>
            <div class="skyquest-bottom-main">
              <div class="skyquest-formation-name">${formationName || '&nbsp;'}</div>
              <div class="skyquest-point-value">${pointHtml || '&nbsp;'}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  };

  const buildSkyQuestCardsHtml = (cards, logos) => {
    if (!cards.length) return '';
    const instructions = `<div class="print-instructions">Print settings: Margins = <b>None</b>, Scale = <b>100%</b> (do NOT use "Fit to page"). Paper = Letter (8.5&times;11).</div>`;
    const perSheet = 9;
    const sheets = [];
    for (let i = 0; i < cards.length; i += perSheet) {
      const chunk = cards.slice(i, i + perSheet);
      const cardsHtml = chunk
        .map((card, idx) => {
          const col = idx % 3;
          const row = Math.floor(idx / 3);
          return `<div class="skyquest-card col${col} row${row}">${buildSkyQuestCardHtml(card, logos)}</div>`;
        })
        .join('');
      sheets.push(`<div class="skyquest-sheet">${cardsHtml}</div>`);
    }
    return instructions + sheets.join('');
  };

  const escapeHtml = (s) =>
    String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const buildBucketFlyerHtml = (items) => {
    const flyerHtml = (p) => {
      const sponsor = p.sponsor || null;
      const logoUrl = (sponsor?.logo || '').trim();
      const useLogo = logoUrl && !logoUrl.includes('drive.google.com');
      const imageUrl = (p.imageUrl || '').trim();
      const nameOrDesc = (p.name || p.description || 'Prize').trim();
      const qty = Math.max(1, Math.floor(Number(p.quantity)) || 1);
      const drawnText =
        qty === 1
          ? 'THIS ITEM WILL BE DRAWN ONCE'
          : `THIS ITEM WILL BE DRAWN ${qty} TIMES`;
      const exp = p.expirationDate;
      const expStr =
        exp &&
        (exp.toDate ? exp.toDate().toISOString().slice(0, 10) : typeof exp === 'string' ? exp.slice(0, 10) : '');
      const validUntil =
        expStr &&
        new Date(expStr + 'T12:00:00').toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
        });
      const conditions = [
        'Order must be placed directly through the manufacturer',
        validUntil ? `Valid until: ${validUntil}` : null,
        "CAN'T BE REDEEMED FOR CASH",
      ].filter(Boolean);
      return `
        <div class="bucket-flyer">
          <div class="bucket-flyer-top">
            ${useLogo ? `<img src="${escapeHtml(logoUrl)}" alt="" class="bucket-flyer-logo" />` : ''}
            ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="" class="bucket-flyer-prize-image" />` : ''}
          </div>
          <div class="bucket-flyer-prize-title">${escapeHtml(nameOrDesc).toUpperCase()}</div>
          <div class="bucket-flyer-instructions">
            Put as many of your raffle tickets as you would like in the box<br />
            1 ticket = 1 chance to win!
          </div>
          <div class="bucket-flyer-drawn">${escapeHtml(drawnText)}</div>
          <hr class="bucket-flyer-hr" />
          <div class="bucket-flyer-conditions">Conditions:</div>
          <div class="bucket-flyer-conditions-list">
            ${conditions.map((c) => `<div>${escapeHtml(c)}</div>`).join('')}
          </div>
          <hr class="bucket-flyer-hr" />
          <div class="bucket-flyer-present">MUST BE PRESENT TO WIN!</div>
          <div class="bucket-flyer-cut-strip">
            <div class="bucket-flyer-cut-strip-inner">
              ${useLogo ? `<img src="${escapeHtml(logoUrl)}" alt="" class="bucket-flyer-cut-logo" />` : ''}
              ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="" class="bucket-flyer-cut-pic" />` : ''}
              <span class="bucket-flyer-cut-prize">${escapeHtml(nameOrDesc)}</span>
            </div>
          </div>
        </div>`;
    };
    const pages = items.map((p) => flyerHtml(p)).join('');
    const bucketFlyerCss = `
      @page { size: 5.5in 8.5in; margin: 0.2in 0.4in 0.4in 0.4in; }
      .bucket-flyer {
        width: 100%; min-height: 7.7in; box-sizing: border-box;
        display: flex; flex-direction: column; align-items: center; text-align: center;
        page-break-after: always; padding: 0 0 0;
      }
      .bucket-flyer:last-child { page-break-after: auto; }
      .bucket-flyer-top {
        display: flex; align-items: center; justify-content: center; gap: 0.3in; flex-wrap: wrap; margin-bottom: 0.25in;
      }
      .bucket-flyer-logo {
        max-width: 1.8in; max-height: 1.8in; width: auto; height: auto; object-fit: contain;
      }
      .bucket-flyer-prize-image {
        max-width: 1.8in; max-height: 1.8in; width: auto; height: auto; object-fit: contain;
      }
      .bucket-flyer-prize-title {
        font-size: 16pt; font-weight: 700; letter-spacing: 0.02em; line-height: 1.2; margin-bottom: 0.2in;
      }
      .bucket-flyer-instructions {
        font-size: 11pt; font-weight: 700; margin-bottom: 0.18in; line-height: 1.35;
      }
      .bucket-flyer-drawn {
        font-size: 12pt; font-weight: 700; color: #c00; margin-bottom: 0.2in; letter-spacing: 0.02em;
      }
      .bucket-flyer-hr {
        width: 100%; border: none; border-top: 1px solid #000; margin: 0.15in 0;
      }
      .bucket-flyer-conditions {
        font-size: 11pt; font-weight: 700; margin-bottom: 0.08in; text-align: center; width: 100%;
      }
      .bucket-flyer-conditions-list {
        font-size: 10pt; font-style: italic; text-align: center; width: 100%; line-height: 1.4;
      }
      .bucket-flyer-present {
        font-size: 14pt; font-weight: 700; letter-spacing: 0.03em; margin-top: 0.15in; padding-top: 0;
      }
      .bucket-flyer-cut-strip {
        margin-top: auto; padding-top: 0.25in; border-top: 2px dashed #666; width: 100%;
      }
      .bucket-flyer-cut-strip-inner {
        display: flex; align-items: center; justify-content: center; gap: 0.2in; flex-wrap: wrap;
        padding: 0.12in 0; width: 100%;
      }
      .bucket-flyer-cut-logo {
        max-width: 0.6in; max-height: 0.5in; width: auto; height: auto; object-fit: contain;
      }
      .bucket-flyer-cut-pic {
        max-width: 0.6in; max-height: 0.5in; width: auto; height: auto; object-fit: contain;
      }
      .bucket-flyer-cut-prize {
        font-size: 8pt; font-weight: 600; max-width: 2in; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
    `;
    return { html: pages, extraCss: bucketFlyerCss };
  };

  const buildEventSignHtml = (dashboardQrDataUrl, whatsappQrDataUrl) => {
    const logoUrl = getLogoUrl();
    const eventTitle = currentEvent ? currentEvent.title || '' : 'Fresh Meet';
    const escape = (s) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    const html = `
      <div class="event-sign">
        <div class="event-sign-header">
          <img src="${logoUrl}" alt="Fresh Meet" class="event-sign-logo" />
          <div class="event-sign-heading-group">
            <div class="event-sign-title">${escape(eventTitle)}</div>
            <div class="event-sign-subtitle">Scan to join the live dashboard</div>
          </div>
        </div>

        <div class="event-sign-main">
          <div class="event-sign-left">
            <div class="event-sign-callout">Scan this code on your phone</div>
            <ul class="event-sign-list">
              <li>See the full <strong>schedule</strong></li>
              <li>Read the event <strong>rules</strong></li>
              <li>Watch the <strong>rules video</strong></li>
              <li>View the live <strong>leaderboard</strong></li>
              <li>Register your <strong>team name &amp; division</strong></li>
            </ul>
          </div>
          <div class="event-sign-qr-main">
            <div class="event-sign-qr-label">Scan for dashboard</div>
            ${dashboardQrDataUrl ? `<img src="${dashboardQrDataUrl}" alt="QR code to dashboard" class="event-sign-qr" />` : ''}
          </div>
        </div>

        <div class="event-sign-whatsapp">
          <div class="event-sign-whatsapp-heading">Fresh Meet WhatsApp group</div>
          <div class="event-sign-whatsapp-sub">Announcements, chit chat, and sharing photos &amp; video from the meet.</div>
          <div class="event-sign-qr-whatsapp">
            <div class="event-sign-qr-label">Scan for WhatsApp chat</div>
            ${whatsappQrDataUrl ? `<img src="${whatsappQrDataUrl}" alt="QR code to WhatsApp announcements &amp; chat" class="event-sign-qr event-sign-qr--small" />` : ''}
          </div>
        </div>
      </div>
    `;
    const extraCss = `
      @page { size: 8.5in 11in; margin: 0.5in; }
      body {
        margin: 0;
        font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        color: #0f172a;
        background: #f8fafc;
      }
      .event-sign {
        width: 100%;
        box-sizing: border-box;
        background: #0f172a;
        color: #e5e7eb;
        border-radius: 16px;
        padding: 0.5in;
        display: flex;
        flex-direction: column;
        gap: 0.3in;
      }
      .event-sign-header {
        display: flex;
        align-items: center;
        gap: 0.3in;
      }
      .event-sign-logo {
        height: 1.2in;
        width: auto;
      }
      .event-sign-heading-group {
        display: flex;
        flex-direction: column;
        gap: 0.12in;
      }
      .event-sign-title {
        font-size: 24pt;
        font-weight: 800;
        letter-spacing: -0.03em;
      }
      .event-sign-subtitle {
        font-size: 11pt;
        font-weight: 500;
        color: #cbd5f5;
      }
      .event-sign-main {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.45in;
      }
      .event-sign-left {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.1in;
        font-size: 12pt;
      }
      .event-sign-callout {
        font-size: 15pt;
        font-weight: 600;
        color: #e5e7eb;
      }
      .event-sign-list {
        margin: 0.05in 0 0;
        padding-left: 0.2in;
      }
      .event-sign-list li {
        margin-bottom: 0.06in;
      }
      .event-sign-qr-main {
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: #020617;
        padding: 0.22in;
        border-radius: 12px;
        gap: 0.08in;
      }
      .event-sign-qr-label {
        font-size: 9pt;
        font-weight: 500;
        color: #e5e7eb;
      }
      .event-sign-qr {
        width: 2.6in;
        height: 2.6in;
      }
      .event-sign-qr--small {
        width: 1.9in;
        height: 1.9in;
      }
      .event-sign-whatsapp {
        margin-top: 0.2in;
        padding-top: 0.2in;
        border-top: 1px solid rgba(148, 163, 184, 0.4);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.08in;
      }
      .event-sign-whatsapp-heading {
        font-size: 11pt;
        font-weight: 600;
      }
      .event-sign-whatsapp-sub {
        font-size: 9pt;
        color: #cbd5f5;
        text-align: center;
        max-width: 4.5in;
      }
      .event-sign-qr-whatsapp {
        margin-top: 0.12in;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.06in;
      }
    `;
    return { html, extraCss };
  };

  const buildPasscodeCardsHtml = (cards, qrDataUrl) => {
    const logoUrl = getLogoUrl();
    const escape = (s) => String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    const cardHtml = (c, col, row) => {
      const teamNum = escape(c.teamNumber);
      const codeUpper = (c.passcode || '').toUpperCase() || '—';
      const qrImg = qrDataUrl ? `<div class="passcode-card-qr"><img src="${qrDataUrl}" alt="Scan for leaderboard" /></div>` : '';
      return `<div class="avery-5371-card col${col} row${row}">
        <div class="passcode-card-left">
          <div class="passcode-card-top">
            <img src="${logoUrl}" alt="Fresh Meet" class="passcode-card-logo" />
            <span class="passcode-card-brand">FRESH MEET</span>
          </div>
          <div class="passcode-card-main">
            <div class="passcode-card-team">TEAM ${teamNum || '—'}</div>
            <div class="passcode-card-divider"></div>
            <div class="passcode-card-code-label">PASSCODE</div>
            <div class="passcode-card-code">${codeUpper}</div>
            <div class="passcode-card-divider"></div>
            <div class="passcode-card-msg">Scan to submit puzzles &amp; view leaderboard</div>
          </div>
          <div class="passcode-card-bottom">TEAM ${teamNum || '—'} | PASSCODE ${codeUpper}</div>
        </div>
        <div class="passcode-card-qr-wrap">${qrImg}</div>
      </div>`;
    };
    const instructions = `<div class="print-instructions">Print settings: Margins = <b>None</b>, Scale = <b>100%</b> (do NOT use "Fit to page"). Paper = Letter (8.5&times;11).</div>`;
    const sheets = [];
    for (let i = 0; i < cards.length; i += PASSCODE_CARDS_PER_SHEET) {
      const chunk = cards.slice(i, i + PASSCODE_CARDS_PER_SHEET);
      const cardsHtml = chunk.map((c, idx) => cardHtml(c, idx % 2, Math.floor(idx / 2))).join('');
      sheets.push(`<div class="avery-5371-sheet">${cardsHtml}</div>`);
    }
    return instructions + sheets.join('');
  };

  const handlePrintPasscodeCards = async () => {
    const max = Math.max(1, Math.min(Number(maxTeamNumber) || DEFAULT_MAX_TEAM_NUMBER, 999));
    const cards = [];
    for (let n = 1; n <= max; n++) {
      const numKey = String(n);
      const passcode = teamPasscodes[numKey] ?? teams.find((t) => String(t.teamNumber) === numKey)?.passcode ?? '';
      cards.push({ teamNumber: n, passcode });
    }
    const dashboardUrl = window.location.origin;
    let qrDataUrl = '';
    try {
      qrDataUrl = await QRCode.toDataURL(dashboardUrl, { width: 200, margin: 1 });
    } catch (err) {
      console.error('QR generation failed:', err);
    }
    const eventTitle = currentEvent ? currentEvent.title || '' : 'Event';
    const title = `${eventTitle} – Passcode Cards`;
    const bodyHtml = buildPasscodeCardsHtml(cards, qrDataUrl);
    const returnUrl = `${window.location.pathname}${window.location.search}`;
    openPrintWindow(title, bodyHtml, getAvery5874BusinessCardCss(), returnUrl);
  };

  const handlePrintEventSign = async () => {
    const dashboardUrl = window.location.origin;
    const whatsappUrl = 'https://chat.whatsapp.com/KWJHq0CSnYAFlenJSqIbjD';
    let dashboardQr = '';
    let whatsappQr = '';
    try {
      dashboardQr = await QRCode.toDataURL(dashboardUrl, { width: 600, margin: 1 });
      whatsappQr = await QRCode.toDataURL(whatsappUrl, { width: 480, margin: 1 });
    } catch (err) {
      console.error('QR generation failed:', err);
    }
    const eventTitle = currentEvent ? currentEvent.title || '' : 'Event';
    const title = `${eventTitle} – Event Sign`;
    const result = buildEventSignHtml(dashboardQr, whatsappQr);
    const returnUrl = `${window.location.pathname}${window.location.search}`;
    openPrintWindow(title, result.html, result.extraCss, returnUrl);
  };

  const buildEnvelopeLabelsHtml = () => {
    // Keep the original wording/format:
    // - Main Clue 1 (for everyone)
    // - Dive 1 + Set A/B/C with sponsors
    // - Dive 4 only (no sponsors)
    // Arrange them on Avery 5160 in the same proportions as the reference sheet:
    // row1 = Main Clue 1, rows2–4 = Dive 1 sets by team range, row5 = Dive 4, then repeat.
    const makeAbbr = (s) => {
      const raw =
        (s.abbreviation || s.abbr || s.shortName || s.name || '')
          .toString()
          .trim();
      if (!raw) return '';
      return raw.slice(0, 3).toUpperCase();
    };

    const assignments = mainClueSetAssignments || {};

    const sponsorsBySet = { A: [], B: [], C: [] };
    sponsors.forEach((s) => {
      const setId = assignments[s.id];
      if (setId !== 'A' && setId !== 'B' && setId !== 'C') return;
      if ((s.showOnDashboard ?? true) === false) return;
      const type = (s.type || '').toString().toLowerCase();
      if (type === 'none') return;
      const abbr = makeAbbr(s);
      if (!abbr) return;
      sponsorsBySet[setId].push(abbr);
    });

    const sponsorsLineForSet = (setId) => {
      const list = sponsorsBySet[setId] || [];
      return list.join(' · ');
    };

    const LABEL_TYPES = {
      CLUE1: {
        title: 'Main Clue 1',
        line2: '',
      },
      // Dive 1 + first question sets (with sponsors from Question Set sponsors)
      D1_A: {
        title: 'Teams 1–16',
        line2: 'Dive 1 + Set A',
        setId: 'A',
      },
      D1_B: {
        title: 'Teams 17–33',
        line2: 'Dive 1 + Set B',
        setId: 'B',
      },
      D1_C: {
        title: 'Teams 34–50',
        line2: 'Dive 1 + Set C',
        setId: 'C',
      },
      // Dive 2 envelopes (per stuffing grid)
      // Teams 1–16: Dive 2 + Set B
      // Teams 17–33: Dive 2 + Set C
      // Teams 34–50: Dive 2 + Set A
      D2_A: {
        title: 'Teams 1–16',
        line2: 'Dive 2 + Set B',
        setId: 'B',
      },
      D2_B: {
        title: 'Teams 17–33',
        line2: 'Dive 2 + Set C',
        setId: 'C',
      },
      D2_C: {
        title: 'Teams 34–50',
        line2: 'Dive 2 + Set A',
        setId: 'A',
      },
      // Dive 3 envelopes (per stuffing grid)
      // Teams 1–16: Dive 3 + Set C
      // Teams 17–33: Dive 3 + Set A
      // Teams 34–50: Dive 3 + Set B
      D3_A: {
        title: 'Teams 1–16',
        line2: 'Dive 3 + Set C',
        setId: 'C',
      },
      D3_B: {
        title: 'Teams 17–33',
        line2: 'Dive 3 + Set A',
        setId: 'A',
      },
      D3_C: {
        title: 'Teams 34–50',
        line2: 'Dive 3 + Set B',
        setId: 'B',
      },
      // Dive 4 only (no sponsors / no set)
      D4: {
        title: 'Dive 4',
        line2: '',
      },
    };

    // Ten rows, three columns. Pattern:
    // 1: CLUE1  | CLUE1  | CLUE1
    // 2: D1_A   | D1_B   | D1_C
    // 3: D2_A   | D2_B   | D2_C
    // 4: D3_A   | D3_B   | D3_C
    // 5: D4     | D4     | D4
    // 6–10: repeat 1–5.
    const rowKinds = [
      ['CLUE1', 'CLUE1', 'CLUE1'],
      ['D1_A', 'D1_B', 'D1_C'],
      ['D2_A', 'D2_B', 'D2_C'],
      ['D3_A', 'D3_B', 'D3_C'],
      ['D4', 'D4', 'D4'],
    ];

    const allRows = [...rowKinds, ...rowKinds];

    const labelForKind = (kind) => {
      const base = LABEL_TYPES[kind];
      if (!base) return null;
      if (!base.setId) return base;
      const sponsorsLine = sponsorsLineForSet(base.setId);
      return {
        ...base,
        sponsors: sponsorsLine,
      };
    };

    const labelHtml = (label) => {
      if (!label) return '';
      const sponsorsLine =
        label.sponsors && label.sponsors.trim()
          ? `<div class="fm-envelope-sponsors">Sponsors: ${label.sponsors.trim()}</div>`
          : '';
      return `
      <div class="avery-5160-label fm-envelope-label">
        <div class="fm-envelope-group">${label.title}</div>
        ${
          label.line2
            ? `<div class="fm-envelope-main"><div class="fm-envelope-main-dive">${label.line2}</div></div>`
            : ''
        }
        ${sponsorsLine}
      </div>`;
    };

    const labelsHtml = allRows
      .flat()
      .map((kind) => labelHtml(labelForKind(kind)))
      .join('');
    const sheetHtml = `<div class="avery-5160-sheet fm-envelope-sheet">${labelsHtml}</div>`;

    const extraCss = `
      @page { size: 8.5in 11in; margin: 0; }
      body { margin: 0; padding: 0; }
      .avery-5160-sheet {
        width: 8.5in;
        height: 11in;
        padding: 0.5in 0.1875in;
        box-sizing: border-box;
        display: grid;
        grid-template-columns: repeat(3, 2.625in);
        grid-template-rows: repeat(10, 1in);
        column-gap: 0.125in;
        row-gap: 0;
        page-break-after: always;
      }
      .avery-5160-sheet:last-child { page-break-after: auto; }
      .avery-5160-label {
        width: 2.625in;
        height: 1in;
        padding: 0.08in 0.1in;
        box-sizing: border-box;
        font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 7pt;
        line-height: 1.2;
      }
      .fm-envelope-label {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
      }
      .fm-envelope-group {
        font-size: 11pt;
        font-weight: 600;
        color: #0f172a;
        margin-bottom: 0.02in;
      }
      .fm-envelope-cashed {
        font-size: 6pt;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #64748b;
        margin-bottom: 0.03in;
      }
      .fm-envelope-main {
        width: 100%;
        margin-bottom: 0.02in;
        color: #020617;
      }
      .fm-envelope-main-dive {
        font-size: 11pt;
        font-weight: 700;
        line-height: 1.1;
      }
    `;

    return { html: sheetHtml, extraCss };
  };

  const handlePrintEnvelopeLabels = () => {
    const eventTitle = currentEvent ? currentEvent.title || '' : 'Event';
    const title = `${eventTitle} – Envelope Labels`;
    const { html, extraCss } = buildEnvelopeLabelsHtml();
    const returnUrl = `${window.location.pathname}${window.location.search}`;
    openPrintWindow(title, html, getAvery5163BagLabelCss() + extraCss, returnUrl);
  };

  /** Build SkyQuest card HTML + CSS for the given selection. Returns null if no formations/counts. */
  const getSkyQuestPrintContent = (toPrint) => {
    const sourceFormations = skyQuestFormations.length ? skyQuestFormations : (SKYQUEST_FORMATIONS || []);
    const formationsById = new Map();
    sourceFormations.forEach((f) => {
      if (!f || !f.id) return;
      formationsById.set(f.id, f);
    });
    const weightedFormations = [];
    let totalWeight = 0;
    formationsById.forEach((formation, id) => {
      const weightRaw = skyQuestFormationCounts[id];
      const weight = Math.max(0, Number.isFinite(Number(weightRaw)) ? Number(weightRaw) : 0);
      if (weight > 0) {
        weightedFormations.push({
          formationName: formation.formationName || '',
          formationImageUrl: formation.formationImageUrl || '',
          weight,
        });
        totalWeight += weight;
      }
    });
    if (!weightedFormations.length) return null;

    const pickFormationByWeight = () => {
      const r = Math.random() * totalWeight;
      let acc = 0;
      for (let i = 0; i < weightedFormations.length; i += 1) {
        acc += weightedFormations[i].weight;
        if (r <= acc) return weightedFormations[i];
      }
      return weightedFormations[weightedFormations.length - 1];
    };

    const cards = [];
    if (toPrint.length) {
      toPrint.forEach((flyer) => {
        const formation = pickFormationByWeight();
        cards.push({
          flyerId: flyer.id,
          participantName: getDisplayName(flyer) || flyer.name || flyer.email || '',
          formationName: formation.formationName || '',
          formationImageUrl: formation.formationImageUrl || '',
          pointValue: skyQuestPointValue,
        });
      });
    }
    const blanksCount = Math.max(0, Math.min(Number(skyQuestBlankWinners) || 0, 200));
    for (let i = 0; i < blanksCount; i += 1) {
      cards.push({
        flyerId: null,
        participantName: '',
        formationName: '',
        formationImageUrl: '',
        pointValue: undefined,
      });
    }
    const perSheet = 9;
    const remainder = cards.length % perSheet;
    if (remainder !== 0) {
      const pad = perSheet - remainder;
      for (let i = 0; i < pad; i += 1) {
        const formation = pickFormationByWeight();
        cards.push({
          flyerId: null,
          participantName: '',
          formationName: formation.formationName || '',
          formationImageUrl: formation.formationImageUrl || '',
          pointValue: skyQuestPointValue,
        });
      }
    }
    const bodyHtml = buildSkyQuestCardsHtml(cards, {
      skyQuestLogoUrl,
      freshMeetLogoUrl: freshMeetSkyQuestLogoUrl,
    });
    const extraCss = getSkyQuestCardCss();
    return { bodyHtml, extraCss, cards };
  };

  const handlePrintSelected = async () => {
    const toPrint = rows.filter((r) => selectedIds.has(r.id));
    if (!toPrint.length) return;

    const eventTitle = currentEvent ? currentEvent.title || '' : 'Event';
    let title = `${eventTitle} – `;
    let bodyHtml = '';
    let extraCss = '';

    if (printType === PRINT_TYPES.BADGES) {
      title += 'Badges';
      bodyHtml = buildBadgesHtml(toPrint);
      extraCss = getAvery5163BadgeCss();
    } else if (printType === PRINT_TYPES.BAG_LABELS) {
      title += 'Bag Labels';
      bodyHtml = buildBagLabelsHtml(toPrint);
      extraCss = getAvery5163BagLabelCss();
    } else if (printType === PRINT_TYPES.RAFFLE) {
      title += 'Raffle Tickets';
      const barcodeByFlyerId = await buildRaffleBarcodeDataUrlByFlyerId(toPrint.map((r) => r.id));
      const result = buildRaffleHtml(toPrint, false, barcodeByFlyerId);
      bodyHtml = result.html;
      extraCss = result.extraCss;
    } else if (printType === PRINT_TYPES.GENERAL_RAFFLE) {
      title += 'General Raffle Tickets';
      const result = buildGeneralRaffleHtml(toPrint);
      bodyHtml = result.html;
      extraCss = result.extraCss;
    } else if (printType === PRINT_TYPES.PLACARDS) {
      title += 'Team Placards';
      const result = buildPlacardsHtml(toPrint);
      bodyHtml = result.html;
      extraCss = result.extraCss;
    } else if (printType === PRINT_TYPES.BUCKET_FLYERS) {
      title += 'Bucket Flyers';
      const result = buildBucketFlyerHtml(toPrint);
      bodyHtml = result.html;
      extraCss = result.extraCss;
    } else if (printType === PRINT_TYPES.SKYQUEST_FRESHLING_CARDS) {
      const skyContent = getSkyQuestPrintContent(toPrint);
      if (!skyContent) return;
      title += 'SkyQuest Freshling Cards';
      bodyHtml = skyContent.bodyHtml;
      extraCss = skyContent.extraCss;
      if (skyContent.cards.length) setSkyQuestPreviewCard(skyContent.cards[0]);
    }

    const returnUrl = `${window.location.pathname}${window.location.search}`;
    openPrintWindow(title, bodyHtml, extraCss, returnUrl);
    if (printType !== PRINT_TYPES.BUCKET_FLYERS && printType !== PRINT_TYPES.SKYQUEST_FRESHLING_CARDS) {
      markAsPrinted(toPrint.map((r) => r.id));
    }
    if (printType === PRINT_TYPES.SKYQUEST_FRESHLING_CARDS) {
      markAsPrinted(toPrint.map((r) => r.id));
    }
  };

  const handleDownloadSkyQuestPdf = async () => {
    const toPrint = rows.filter((r) => selectedIds.has(r.id));
    const content = getSkyQuestPrintContent(toPrint);
    if (!content) {
      setSkyQuestFormationMessage('Set at least one formation count in the table above, then try again.');
      return;
    }
    setSkyQuestFormationMessage('');
    setSkyQuestPdfLoading(true);
    try {
      const eventTitle = currentEvent ? currentEvent.title || '' : 'Event';
      const filename = `${(eventTitle || 'SkyQuest').replace(/[^a-z0-9-]/gi, '-')}-freshling-cards.pdf`;
      await buildSkyQuestPdf(content.bodyHtml, content.extraCss, filename);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('SkyQuest PDF build failed:', err);
    } finally {
      setSkyQuestPdfLoading(false);
    }
  };

  const handlePrintBlanks = async () => {
    const n = Math.max(1, Math.min(blankCount || 5, 100));
    const eventTitle = currentEvent ? currentEvent.title || '' : 'Event';
    let title = `${eventTitle} – `;
    let bodyHtml = '';
    let extraCss = '';

    if (printType === PRINT_TYPES.BADGES || printType === PRINT_TYPES.BAG_LABELS) {
      const blanks = Array(n).fill({ type: 'freshling' });
      if (printType === PRINT_TYPES.BADGES) {
        title += 'Badges (blanks)';
        bodyHtml = buildBadgesHtml(blanks, true);
        extraCss = getAvery5163BadgeCss();
      } else {
        title += 'Bag Labels (blanks)';
        bodyHtml = buildBagLabelsHtml(blanks, true);
        extraCss = getAvery5163BagLabelCss();
      }
    } else if (printType === PRINT_TYPES.RAFFLE) {
      title += 'Raffle Tickets (blanks)';
      const result = buildRaffleHtml(n, true, null);
      bodyHtml = result.html;
      extraCss = result.extraCss;
    } else if (printType === PRINT_TYPES.GENERAL_RAFFLE) {
      title += 'General Raffle Tickets (blanks)';
      const result = buildGeneralRaffleHtml(n, true);
      bodyHtml = result.html;
      extraCss = result.extraCss;
    } else if (printType === PRINT_TYPES.PLACARDS) {
      title += 'Team Placards (blanks)';
      const blanks = Array(n).fill({ teamNumber: '', teamName: '', mentorName: '', freshlingNames: [] });
      const result = buildPlacardsHtml(blanks, true);
      bodyHtml = result.html;
      extraCss = result.extraCss;
    }

    const returnUrl = `${window.location.pathname}${window.location.search}`;
    openPrintWindow(title, bodyHtml, extraCss, returnUrl);
  };

  return (
    <div className="printables">
      <header className="printables-header">
        <h2>Printables</h2>
        {dataError && <p className="printables-error">{dataError}</p>}
      </header>

      <div className="printables-controls">
        <label>
          Event:{' '}
          <select
            className="printables-select"
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            disabled={eventsLoading}
          >
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.title}
              </option>
            ))}
          </select>
        </label>
        <label>
          Print type:{' '}
          <select className="printables-select" value={printType} onChange={(e) => setPrintType(e.target.value)}>
            <option value={PRINT_TYPES.BADGES}>Badges</option>
            <option value={PRINT_TYPES.BAG_LABELS}>Bag Labels</option>
            <option value={PRINT_TYPES.RAFFLE}>Raffle Tickets</option>
            <option value={PRINT_TYPES.GENERAL_RAFFLE}>General Raffle Tickets</option>
            <option value={PRINT_TYPES.PLACARDS}>Team Placards</option>
            <option value={PRINT_TYPES.SKYQUEST_FRESHLING_CARDS}>SkyQuest Freshling Cards</option>
            <option value={PRINT_TYPES.BUCKET_FLYERS}>Bucket Flyers</option>
            <option value={PRINT_TYPES.PASSCODE_CARDS}>Passcode Cards</option>
            <option value={PRINT_TYPES.EVENT_SIGN}>Event QR sign</option>
            <option value={PRINT_TYPES.ENVELOPE_LABELS}>Envelope labels (HQ grid)</option>
          </select>
        </label>
        {printType === PRINT_TYPES.PASSCODE_CARDS && (
          <label>
            Max team number:{' '}
            <input
              type="number"
              min={1}
              max={999}
              value={maxTeamNumber}
              onChange={(e) => setMaxTeamNumber(parseInt(e.target.value, 10) || DEFAULT_MAX_TEAM_NUMBER)}
              style={{ width: '4rem' }}
            />
          </label>
        )}
        {(printType === PRINT_TYPES.BADGES || printType === PRINT_TYPES.BAG_LABELS) && (
          <label>
            Type:{' '}
            <select className="printables-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="all">All</option>
              <option value="freshling">Freshlings only</option>
              <option value="mentor">Mentors only</option>
              {printType === PRINT_TYPES.BAG_LABELS && (
                <option value="volunteer">Volunteers only</option>
              )}
            </select>
          </label>
        )}
        {printType !== PRINT_TYPES.PASSCODE_CARDS && printType !== PRINT_TYPES.BUCKET_FLYERS && printType !== PRINT_TYPES.EVENT_SIGN && (
          <label>
            <input
              type="checkbox"
              checked={filterUnprinted}
              onChange={(e) => setFilterUnprinted(e.target.checked)}
            />
            Unprinted only
          </label>
        )}
        {printType === PRINT_TYPES.BAG_LABELS && (
          <label>
            <input
              type="checkbox"
              checked={filterShirtMismatch}
              onChange={(e) => setFilterShirtMismatch(e.target.checked)}
            />
            Shirt mismatch only
          </label>
        )}
      </div>

      {printType === PRINT_TYPES.SKYQUEST_FRESHLING_CARDS && (
        <div className="printables-skyquest-config">
          <div className="printables-skyquest-section">
            <div className="printables-skyquest-section-title">Run settings</div>
            <div className="printables-skyquest-config-row">
            <label>
              Point value:{' '}
              <input
                type="number"
                step="0.25"
                min="0"
                value={skyQuestPointValue}
                onChange={(e) => setSkyQuestPointValue(parseFloat(e.target.value) || 0)}
              />
            </label>
            <label>
              Blank winner cards:{' '}
              <input
                type="number"
                min={0}
                max={200}
                value={skyQuestBlankWinners}
                onChange={(e) => setSkyQuestBlankWinners(parseInt(e.target.value, 10) || 0)}
              />
            </label>
          </div>
          </div>

          <div className="printables-skyquest-section">
            <div className="printables-skyquest-section-title">Logos</div>
            <div className="printables-skyquest-config-row">
            <label className="printables-skyquest-logo-field">
              <span className="printables-skyquest-logo-label">SkyQuest logo:</span>
              {skyQuestLogoUrl ? (
                <img src={skyQuestLogoUrl} alt="SkyQuest logo" className="printables-skyquest-logo-preview" />
              ) : (
                <span className="printables-skyquest-logo-placeholder">No logo</span>
              )}
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={async (e) => {
                  const file = e.target.files && e.target.files[0];
                  if (!file) return;
                  const ext = (file.name.split('.').pop() || 'png').toLowerCase();
                  const storageRef = ref(storage, `skyQuest/logos/skyquest.${ext}`);
                  try {
                    await uploadBytes(storageRef, file);
                    const url = await getDownloadURL(storageRef);
                    setSkyQuestLogoUrl(url);
                    await setDoc(
                      doc(firestore, 'skyQuestConfig', 'global'),
                      { skyQuestLogoUrl: url },
                      { merge: true }
                    );
                  } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('SkyQuest logo upload failed:', err);
                  }
                }}
              />
            </label>
            <label className="printables-skyquest-logo-field">
              <span className="printables-skyquest-logo-label">Fresh Meet logo:</span>
              {freshMeetSkyQuestLogoUrl ? (
                <img src={freshMeetSkyQuestLogoUrl} alt="Fresh Meet logo" className="printables-skyquest-logo-preview" />
              ) : (
                <span className="printables-skyquest-logo-placeholder">No logo</span>
              )}
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={async (e) => {
                  const file = e.target.files && e.target.files[0];
                  if (!file) return;
                  const ext = (file.name.split('.').pop() || 'png').toLowerCase();
                  const storageRef = ref(storage, `skyQuest/logos/fresh-meet.${ext}`);
                  try {
                    await uploadBytes(storageRef, file);
                    const url = await getDownloadURL(storageRef);
                    setFreshMeetSkyQuestLogoUrl(url);
                    await setDoc(
                      doc(firestore, 'skyQuestConfig', 'global'),
                      { freshMeetLogoUrl: url },
                      { merge: true }
                    );
                  } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('Fresh Meet logo upload failed:', err);
                  }
                }}
              />
            </label>
          </div>
          </div>

          <div className="printables-skyquest-section">
            <div className="printables-skyquest-section-title">Formations</div>
            {skyQuestFormationMessage && (
              <div className="printables-skyquest-formation-message" role="alert">
                {skyQuestFormationMessage}
              </div>
            )}
            <div className="printables-skyquest-formation-add-row">
              <input
                type="text"
                placeholder="New formation name (e.g. PHALANX)"
                value={newSkyQuestFormationName}
                onChange={(e) => {
                  setNewSkyQuestFormationName(e.target.value);
                  if (skyQuestFormationMessage) setSkyQuestFormationMessage('');
                }}
              />
              <label className="printables-skyquest-upload-btn">
                <span>Upload image…</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  disabled={creatingSkyQuestFormation}
                  onChange={async (e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) return;
                    if (!newSkyQuestFormationName.trim()) {
                      setSkyQuestFormationMessage('Enter a formation name above first (e.g. PHALANX), then pick an image.');
                      return;
                    }
                    setSkyQuestFormationMessage('');
                    const nameRaw = newSkyQuestFormationName.trim();
                    const id = nameRaw
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, '-')
                      .replace(/^-+|-+$/g, '')
                      .slice(0, 50) || 'formation';
                    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
                    const storageRef = ref(storage, `skyQuest/formations/${id}.${ext}`);
                    setCreatingSkyQuestFormation(true);
                    try {
                      await uploadBytes(storageRef, file);
                      const url = await getDownloadURL(storageRef);
                      const data = {
                        formationName: nameRaw,
                        formationImageUrl: url,
                      };
                      await setDoc(doc(firestore, 'skyQuestFormations', id), data, { merge: true });
                      setSkyQuestFormations((prev) => {
                        const existingIndex = prev.findIndex((f) => f.id === id);
                        if (existingIndex >= 0) {
                          const next = [...prev];
                          next[existingIndex] = { id, ...data };
                          return next;
                        }
                        return [...prev, { id, ...data }];
                      });
                      setNewSkyQuestFormationName('');
                    } catch (err) {
                      // eslint-disable-next-line no-console
                      console.error('SkyQuest formation upload failed:', err);
                    } finally {
                      setCreatingSkyQuestFormation(false);
                      if (e.target) {
                        e.target.value = '';
                      }
                    }
                  }}
                />
              </label>
            </div>
            <div className="printables-skyquest-formations">
              <div className="printables-skyquest-formations-header">
                <span>Formation</span>
                <span>Image</span>
                <span>Weight in run</span>
              </div>
              {(skyQuestFormations.length ? skyQuestFormations : (SKYQUEST_FORMATIONS || [])).map((f) => (
                <div key={f.id || f.formationName} className="printables-skyquest-formation-row">
                  <div className="printables-skyquest-formation-name">
                    {f.formationName || f.id || 'Formation'}
                  </div>
                  <div className="printables-skyquest-formation-image">
                    {f.formationImageUrl ? (
                      <img src={f.formationImageUrl} alt={f.formationName || f.id || 'Formation'} />
                    ) : (
                      <span className="printables-skyquest-formation-image-placeholder">No image</span>
                    )}
                  </div>
                  <div className="printables-skyquest-formation-count">
                    <input
                      type="number"
                      min={0}
                      max={999}
                      value={skyQuestFormationCounts[f.id] ?? ''}
                      onChange={(e) =>
                        setSkyQuestFormationCounts((prev) => ({
                          ...prev,
                          [f.id]: e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0,
                        }))
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="printables-toolbar">
        {printType === PRINT_TYPES.PASSCODE_CARDS ? (
          <button
            type="button"
            className="btn-fm-primary"
            onClick={handlePrintPasscodeCards}
            disabled={dataLoading}
          >
            Print passcode cards (teams 1–{Math.max(1, Math.min(Number(maxTeamNumber) || DEFAULT_MAX_TEAM_NUMBER, 999))})
          </button>
        ) : printType === PRINT_TYPES.EVENT_SIGN ? (
          <button
            type="button"
            className="btn-fm-primary"
            onClick={handlePrintEventSign}
            disabled={dataLoading}
          >
            Print event QR sign
          </button>
        ) : printType === PRINT_TYPES.ENVELOPE_LABELS ? (
          <button
            type="button"
            className="btn-fm-primary"
            onClick={handlePrintEnvelopeLabels}
            disabled={dataLoading}
          >
            Print envelope labels
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn-fm-primary"
              onClick={handlePrintSelected}
              disabled={!selectedIds.size || updating}
            >
              Print selected ({selectedIds.size})
            </button>
            {printType === PRINT_TYPES.SKYQUEST_FRESHLING_CARDS && (
              <button
                type="button"
                className="printables-btn-ghost"
                onClick={handleDownloadSkyQuestPdf}
                disabled={!selectedIds.size || updating || skyQuestPdfLoading}
                title="Generate a PDF for accurate printing (same layout as print, exact dimensions)"
              >
                {skyQuestPdfLoading ? 'Building PDF…' : 'Download PDF'}
              </button>
            )}
            <button type="button" className="printables-btn-ghost" onClick={toggleSelectAll}>
              {selectedIds.size === rows.length ? 'Deselect all' : 'Select all'}
            </button>
            {printType !== PRINT_TYPES.BUCKET_FLYERS && (
              <>
                <button
                  type="button"
                  className="printables-btn-ghost"
                  onClick={handleResetSelected}
                  disabled={!selectedIds.size || updating}
                  title="Clear printed status for selected"
                >
                  Reset selected
                </button>
                <button
                  type="button"
                  className="printables-btn-ghost"
                  onClick={handleResetAll}
                  disabled={!printedIds.length || updating}
                  title="Clear printed status for all printed items"
                >
                  Reset all ({printedIds.length})
                </button>
              </>
            )}
          </>
        )}
      </div>

      {printType === PRINT_TYPES.PASSCODE_CARDS ? (
        <p className="printables-empty">
          Avery 5874 business cards (2″ × 3.5″), 10 per sheet (2×5). Text vertical. Cards for teams 1 through{' '}
          {Math.max(1, Math.min(Number(maxTeamNumber) || DEFAULT_MAX_TEAM_NUMBER, 999))} with passcode and QR to the
          public dashboard. Use blank passcode for teams not yet assigned.
        </p>
      ) : printType === PRINT_TYPES.EVENT_SIGN ? (
        <p className="printables-empty">
          Single 8.5×11 sheet with Fresh Meet branding and a large QR code that links to the main dashboard site so
          people can view the schedule, rules, leaderboard, and register their team name/division.
        </p>
      ) : printType === PRINT_TYPES.ENVELOPE_LABELS ? (
        <p className="printables-empty">
          Avery 5163 labels for the six HQ envelope grid positions (team group × cashed set). Each label shows the
          team group, which set&apos;s chips are being cashed, the next question set color band, and the sponsor
          abbreviations for that next set.
        </p>
      ) : dataLoading ? (
        <p>Loading…</p>
      ) : !rows.length ? (
        <p className="printables-empty">
          {filterUnprinted
            ? 'No unprinted items. Clear "Unprinted only" to see all.'
            : 'No data for this event and print type.'}
        </p>
      ) : (
        <div className="printables-table-container">
          <table className="printables-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={rows.length > 0 && selectedIds.size === rows.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                {printType === PRINT_TYPES.BUCKET_FLYERS ? (
                  <>
                    <th>Prize</th>
                    <th>Sponsor</th>
                  </>
                ) : printType === PRINT_TYPES.PLACARDS ? (
                  <>
                    <th>Team</th>
                    <th>Mentor</th>
                    <th>Freshlings</th>
                  </>
                ) : (
                  <>
                    <th>Name</th>
                    <th>Team</th>
                    <th>Type</th>
                  </>
                )}
                {printType !== PRINT_TYPES.BUCKET_FLYERS && (
                  <>
                    <th>Printed</th>
                    <th>Reset</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleSelect(row.id)}
                    />
                  </td>
                  {printType === PRINT_TYPES.BUCKET_FLYERS ? (
                    <>
                      <td>{row.name || row.description || '—'}</td>
                      <td>{row.sponsor?.name || '—'}</td>
                    </>
                  ) : printType === PRINT_TYPES.PLACARDS ? (
                    <>
                      <td>
                        {row.teamNumber}
                        {row.teamName ? ` – ${row.teamName}` : ''}
                      </td>
                      <td>{row.mentorName}</td>
                      <td>{row.freshlingNames?.join(', ') || '—'}</td>
                    </>
                  ) : (
                    <>
                      <td>{getDisplayName(row) || row.name || row.email || '—'}</td>
                      <td>
                        {teamPairingByEmail.get((row.email || '').toString().trim().toLowerCase()) || '—'}
                      </td>
                      <td>{row.type === 'volunteer' ? 'Volunteer' : (row.type || '—')}</td>
                    </>
                  )}
                  {printType !== PRINT_TYPES.BUCKET_FLYERS && (
                    <>
                      <td>
                        <span
                          className={`printables-status ${isPrinted(row, printType) ? 'printed' : ''}`}
                        >
                          {isPrinted(row, printType) ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>
                        {isPrinted(row, printType) && (
                          <button
                            type="button"
                            className="printables-reset-btn"
                            onClick={() => resetPrinted([row.id])}
                            disabled={updating}
                            title="Reset printed status"
                          >
                            Reset
                          </button>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {printType === PRINT_TYPES.SKYQUEST_FRESHLING_CARDS && skyQuestPreviewCard && (
        <div className="printables-skyquest-preview">
          <div className="printables-skyquest-preview-label">Single-card preview</div>
          <div
            className="printables-skyquest-preview-card"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: buildSkyQuestCardHtml(skyQuestPreviewCard, {
                skyQuestLogoUrl,
                freshMeetLogoUrl: freshMeetSkyQuestLogoUrl,
              }),
            }}
          />
        </div>
      )}

      {printType !== PRINT_TYPES.PASSCODE_CARDS && printType !== PRINT_TYPES.BUCKET_FLYERS && (
        <div className="printables-blanks-row">
          <label>
            Print blanks:
            <input
              type="number"
              min={1}
              max={100}
              value={blankCount}
              onChange={(e) => setBlankCount(parseInt(e.target.value, 10) || 5)}
            />
            {printType === PRINT_TYPES.BADGES || printType === PRINT_TYPES.BAG_LABELS
              ? ' labels'
              : printType === PRINT_TYPES.RAFFLE || printType === PRINT_TYPES.GENERAL_RAFFLE
                ? ' tickets'
                : ' placards'}
          </label>
          <button type="button" className="printables-btn-ghost" onClick={handlePrintBlanks}>
            Print blanks
          </button>
        </div>
      )}
    </div>
  );
}

export default Printables;
